import { throttle } from 'es-toolkit';
import { useEffect, useMemo, useRef, useState } from 'react';

import { isNotNullable, isNullable } from '@flipster/utils/nullable';

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const useThrottleCallback = <T extends (...args: any[]) => void>(cb?: T, delay: number = 300) => {
  const cbRef = useRef(cb);
  cbRef.current = cb;

  return useMemo(
    () => throttle((...args: Parameters<T>) => cbRef.current?.(...args), delay, { edges: ['leading'] }),
    [delay],
  );
};

/**
 * note: If staled value is nullable and fresh one is not nullable, update immediately.
 */
export function useThrottleValue<T>(value: T, interval = 200): T {
  const [throttledValue, setThrottledValue] = useState<T>(value);
  const lastExecuted = useRef<number>(Date.now());

  if (isNullable(throttledValue) && isNotNullable(value)) {
    setThrottledValue(value);
  }

  useEffect(() => {
    if (Date.now() >= lastExecuted.current + interval) {
      lastExecuted.current = Date.now();
      setThrottledValue(value);
      return undefined;
    }
    const timerId = setTimeout(() => {
      lastExecuted.current = Date.now();
      setThrottledValue(value);
    }, interval);
    return () => {
      clearTimeout(timerId);
    };
  }, [value, interval]);

  return throttledValue;
}
